import { useNavigate } from 'react-router-dom';
import type { Invoice } from '@catalyst-psa/shared';

import { Badge } from '../../components/ui/Badge.js';
import { DataState } from '../../components/ui/DataState.js';
import { formatDate } from '../../utils/date.js';
import { useInvoices } from '../invoices/api.js';
import {
  INVOICE_SOURCE_LABELS,
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_TONES,
} from '../invoices/status.js';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const HEAD_CLASS = 'px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-500';
const CELL_CLASS = 'px-3 py-2 text-sm text-slate-700';

export function ProjectInvoicesTab({ projectId }: { projectId: string }): JSX.Element {
  const navigate = useNavigate();
  const query = useInvoices({ page: 1, pageSize: 200, projectId });
  const rows = query.data?.data ?? [];
  const total = rows.reduce((sum, inv) => sum + Number(inv.total), 0);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-800">Invoices</h2>
        {rows.length > 0 ? (
          <p className="text-sm text-slate-500">
            {rows.length} invoice{rows.length === 1 ? '' : 's'} · {currency.format(total)}
          </p>
        ) : null}
      </div>
      <div className="overflow-hidden rounded-lg border border-slate-200">
        <table className="w-full border-collapse">
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              {['Number', 'Kind', 'Status', 'Invoice date', 'Due', 'Total'].map((h) => (
                <th key={h} className={HEAD_CLASS}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={6}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              isEmpty={rows.length === 0}
              emptyTitle="No invoices for this project."
              onRetry={() => void query.refetch()}
            >
              {rows.map((inv: Invoice) => (
                <tr
                  key={inv.id}
                  onClick={() => navigate(`/invoices/${inv.id}`)}
                  className="cursor-pointer hover:bg-slate-50"
                >
                  <td className={`${CELL_CLASS} font-medium text-slate-900`}>{inv.number}</td>
                  <td className={CELL_CLASS}>{INVOICE_SOURCE_LABELS[inv.source]}</td>
                  <td className={CELL_CLASS}>
                    <Badge tone={INVOICE_STATUS_TONES[inv.status]}>
                      {INVOICE_STATUS_LABELS[inv.status]}
                    </Badge>
                  </td>
                  <td className={CELL_CLASS}>{formatDate(inv.issueDate)}</td>
                  <td className={CELL_CLASS}>{formatDate(inv.dueDate)}</td>
                  <td className={`${CELL_CLASS} tabular-nums`}>
                    {currency.format(Number(inv.total))}
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>
      </div>
    </div>
  );
}
